import { useEffectiveSetting, useUpsertSetting } from '@/shared/api/settings';

/**
 * ADR-0078 (Open Decisions Q153, John 25 Oct 2026): text criteria on a PowerHouse search window
 * match "starts with" unless the user ticked Options > "Search Exact Match".
 *
 * PB: `w_phfc_search.dw_query::ue_zoom_fix_select` turns each quoted `= 'x'` into `LIKE 'x%'`
 * unless `f_get_menu_by_type('option', 'searchexpert').checked` (w_phfc_search.srw:361-385,
 * w_search.srw:159-190). The menu item is m_phfc_main.srm:9436-9463 ("Search Exact &Match",
 * tag Options.SearchExpert); it is read at frame open (w_phfc_frame.srw:66-68) and saved on click
 * via `iuo_phfc_local_set.of_set_usr('Window', 'ExpertMode', 'On'|'Off')` — per user (and
 * workstation) in the local INI file, or in dbxx.ini_parameters when the client's
 * Client/INISettings is 'Database' (uo_phfc_local_set.sru:73-144, uo_global_var_cntr.sru:171).
 * PH10 has no local INI, so the option lives in the user's PH10 settings
 * (dbxx.ph10_usr_setting, the INI/registry replacement) under PB's own section/key and values.
 *
 * The rewrite itself happens server-side (SearchTextMatch.java); a screen only names its typed
 * text criteria (`_textCrit`) and passes the option (`_exact=1`).
 */
export const SEARCH_EXACT_MATCH_SECTION = 'Window';
export const SEARCH_EXACT_MATCH_KEY = 'ExpertMode';

/** Reserved browse params (SearchTextMatch.TEXT_PARAMS / EXACT_PARAM). */
export const TEXT_CRITERIA_PARAM = '_textCrit';
export const EXACT_MATCH_PARAM = '_exact';

/**
 * Every PowerHouse window that descends from `w_phfc_search` and so carries the rewrite (the
 * whole tree inherits it: none overrides `ue_zoom_fix_select` without calling super). Derived
 * from PB-PH-Source-7-7-26 `global type <w> from <ancestor>` chains; abstract ancestors
 * (w_search, w_query_base, w_query_base_dbl) and `_bak` copies left out.
 */
export const PB_SEARCH_WINDOWS: ReadonlySet<string> = new Set([
  'w_action_log', 'w_adjust_search', 'w_appointment_search', 'w_auto_rstk',
  'w_bill_event_activity', 'w_bill_event_periodic', 'w_bill_summary_search',
  'w_bill_transaction_search', 'w_cart_assign_search', 'w_change_log_legacy', 'w_chold',
  'w_content_hold_adjust', 'w_costing_by_shipment_search', 'w_cust_ddw_header_search',
  'w_customer_master_search', 'w_customer_shipto_search', 'w_delete_open_orders',
  'w_deleted_orders', 'w_email_requests', 'w_exception', 'w_gen_rep_sqle', 'w_global_item_maint',
  'w_global_location_assignment', 'w_global_location_maint', 'w_global_order_line_maint',
  'w_global_order_maint', 'w_global_receipt_line_maint', 'w_global_receipt_maint',
  'w_global_velocity_update', 'w_hi_alarms', 'w_hist_search', 'w_hold_adj_comment_search',
  'w_if_download_ship_search', 'w_if_ul_item_search', 'w_if_ul_order_status_search',
  'w_if_ul_orders_search', 'w_if_ul_ordshipment_search', 'w_if_ul_receipts_search',
  'w_if_ul_ship_maintenance_search', 'w_if_ul_transaction_maintenance_search',
  'w_if_ul_truckship_search', 'w_inbound_asn_search', 'w_interface_services_search',
  'w_invoice_search', 'w_item_cfg_batch_clone', 'w_item_cost_search', 'w_item_search',
  'w_kit_run_search', 'w_labor_task_search', 'w_location_search', 'w_lost_found_search',
  'w_manager_authorization_query', 'w_move_inventory_search', 'w_order_allocation',
  'w_order_assign_search', 'w_order_assign_users_search', 'w_order_delivery_proof',
  'w_order_reallocation', 'w_order_search', 'w_order_status_dashboard', 'w_order_status_history',
  'w_order_tag_search', 'w_orderstat', 'w_pa_request_bulk', 'w_pa_request_bulk_search',
  'w_phfc_report_search', 'w_phfc_ws_saved_report_search', 'w_pick_ticket_assign_search',
  'w_pre_receipt_search', 'w_prod_delv_hist_search', 'w_production_run_search',
  'w_putwall_view_by_batch', 'w_qc_activity_search', 'w_rateshop_order', 'w_receipt_line_search',
  'w_receipt_lines_add_sub_pop', 'w_receipt_search', 'w_receipt_shipto_search', 'w_report_search',
  'w_rstk_request', 'w_rtn_process_order', 'w_ship_misc_search', 'w_trailer_search',
  'w_trashed_pallets_search', 'w_truck_add_pickgroup', 'w_truck_inout_search', 'w_truck_search',
  'w_usr_login_log', 'w_vendor_price_quote_search', 'w_wave_load_plan_search', 'w_waves',
  'w_wf_process_search', 'w_work_search', 'w_ws_saved_report_search',
]);

/** True when `pbWindow` (a PB window class, any case) is a PowerHouse search window. */
export function isPbSearchWindow(pbWindow: string | null | undefined): boolean {
  if (!pbWindow) return false;
  const m = /^w_[a-z0-9_]+/i.exec(pbWindow.trim());
  return m ? PB_SEARCH_WINDOWS.has(m[0].toLowerCase()) : false;
}

/**
 * Search windows whose own Search rewrite is gated on Options > "Search Auto Zoom", not on
 * "Search Exact Match": w_hist_search (dw_query::ue_zoom, w_hist_search.srw:699-713) and
 * w_invoice_search (w_invoice_search.srw:581-595) carry their own copy of the rewrite and never
 * read the Exact Match option, so on them a typed value is ALWAYS "starts with" while Auto Zoom
 * is off (its default). PH10 has no Auto Zoom option, so these screens ignore Search Exact Match.
 * (#7212 LINK1: User "LINK" must find LINK1's rows on History Search.)
 */
export const PB_SEARCH_EXACT_OPTION_IGNORED: ReadonlySet<string> = new Set([
  'w_hist_search',
  'w_invoice_search',
]);

/** True when the user's Search Exact Match option governs `pbWindow`'s typed text criteria. */
export function honoursSearchExactMatch(pbWindow: string | null | undefined): boolean {
  if (!pbWindow || !isPbSearchWindow(pbWindow)) return false;
  const m = /^w_[a-z0-9_]+/i.exec(pbWindow.trim());
  return m ? !PB_SEARCH_EXACT_OPTION_IGNORED.has(m[0].toLowerCase()) : false;
}

/**
 * The user's Search Exact Match option. Off until the setting says 'On' (PB default Off,
 * w_phfc_frame.srw:66). `setExact` saves it — PB saves on every click of the menu item.
 */
export function useSearchExactMatch(): {
  exact: boolean;
  loaded: boolean;
  setExact: (on: boolean) => void;
  saving: boolean;
} {
  const q = useEffectiveSetting(SEARCH_EXACT_MATCH_SECTION, SEARCH_EXACT_MATCH_KEY);
  const upsert = useUpsertSetting();
  return {
    exact: (q.data ?? '').trim().toUpperCase() === 'ON',
    loaded: !q.isLoading,
    saving: upsert.isPending,
    setExact: (on: boolean) =>
      upsert.mutate({
        category: SEARCH_EXACT_MATCH_SECTION,
        key: SEARCH_EXACT_MATCH_KEY,
        value: on ? 'On' : 'Off',
      }),
  };
}

/**
 * Put the search-text markers on a browse request: the typed text criteria params, and the exact
 * option when it is on. Nothing is added when the screen names no text criteria, so a request
 * from a non-search screen is byte-identical to before.
 */
export function applySearchTextMatch(
  p: URLSearchParams,
  textParams: readonly string[],
  exact: boolean,
): void {
  if (textParams.length === 0) return;
  p.set(TEXT_CRITERIA_PARAM, textParams.join(','));
  if (exact) p.set(EXACT_MATCH_PARAM, '1');
}
